import { useState } from 'react'
import { formatMoney } from '../../lib/formatters'
import {
  confirmWeeklyCosts,
  dismissWeeklyCostPrompt,
  proposeWeeklyCosts,
  type WeekForecast,
  type WeeklyCostPrompt,
} from '../../api/weeklyCosts'
import SorenWeekVerdictCard from './SorenWeekVerdictCard'

const FIELDS = [
  ['payroll', 'Labor'],
  ['subs', 'Subs'],
  ['materials', 'Materials'],
  ['other', 'Other'],
  ['fuel', 'Fuel'],
  ['credit_cards', 'Credit cards'],
] as const

type AmountKey = typeof FIELDS[number][0]

export default function SorenAsksFlow({
  prompt,
  onDone,
  onOpenCostsWeek,
}: {
  prompt: WeeklyCostPrompt
  onDone: () => void
  onOpenCostsWeek?: (weekStart: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forecast, setForecast] = useState<WeekForecast | null>(null)
  const [editingAmounts, setEditingAmounts] = useState(false)
  const reply = prompt.pending_reply
  const proposal = reply || prompt.proposal
  if (!proposal || (!prompt.show && !reply)) return null
  const lockedProposal = proposal
  const inferred = new Set(lockedProposal.inferred || [])

  const [amounts, setAmounts] = useState<Record<AmountKey, string>>(() => ({
    payroll: String(Number(lockedProposal.payroll) || 0),
    subs: String(Number(lockedProposal.subs) || 0),
    materials: String(Number(lockedProposal.materials) || 0),
    other: String(Number(lockedProposal.other) || 0),
    fuel: String(Number(lockedProposal.fuel) || 0),
    credit_cards: String(Number(lockedProposal.credit_cards) || 0),
  }))

  if (forecast) {
    return (
      <div style={{ margin: '12px 20px', padding: 14, border: '1px solid var(--line)', borderRadius: 12 }}>
        <SorenWeekVerdictCard
          forecast={forecast}
          onEditCosts={() => {
            onOpenCostsWeek?.(prompt.week_start)
            onDone()
          }}
        />
      </div>
    )
  }

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      const payroll = Number(amounts.payroll) || 0
      const subs = Number(amounts.subs) || 0
      const materials = Number(amounts.materials) || 0
      const other = Number(amounts.other) || 0
      const amountsChanged = editingAmounts || FIELDS.some(([key]) => (
        Number(amounts[key]) || 0
      ) !== (Number(lockedProposal[key]) || 0))
      const id = reply && !amountsChanged
        ? reply.id
        : (await proposeWeeklyCosts({
          week_start: prompt.week_start,
          payroll,
          subs,
          materials,
          other,
          fuel: Number(amounts.fuel) || 0,
          credit_cards: Number(amounts.credit_cards) || 0,
        })).id
      const saved = await confirmWeeklyCosts(id)
      if (saved.forecast) {
        setForecast(saved.forecast)
      } else {
        onDone()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save costs')
    } finally {
      setBusy(false)
    }
  }

  async function dismiss() {
    setBusy(true)
    try {
      await dismissWeeklyCostPrompt(prompt.week_start)
      onDone()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="soren-weekly-cost-prompt" style={{ margin: '12px 20px', padding: 14, border: '1px solid var(--line)', borderRadius: 12 }}>
      <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>
        {reply
          ? `You replied: ${reply.excerpt || '…'} → proposed costs`
          : "What's going out this week?"}
      </p>
      {!editingAmounts ? (
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--ink4)' }} data-testid="soren-weekly-cost-readonly">
          {FIELDS.map(([key, label]) => (
            <span key={key} style={{ display: 'inline-block', marginRight: 10 }}>
              {label} {formatMoney(Number(lockedProposal[key]) || 0)}
              {inferred.has(key) ? ' (inferred)' : ''}
            </span>
          ))}
        </p>
      ) : (
        <div style={{ marginBottom: 12 }} data-testid="soren-weekly-cost-editing">
          {FIELDS.map(([key, label]) => (
            <label key={key} style={{ display: 'block', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--ink4)' }}>{label}</span>
              <input
                type="text"
                inputMode="decimal"
                value={amounts[key]}
                onChange={(e) => setAmounts((a) => ({ ...a, [key]: e.target.value }))}
                data-testid={`soren-weekly-cost-${key}`}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)' }}
              />
            </label>
          ))}
        </div>
      )}
      {error && <p style={{ color: '#b91c1c', fontSize: 13 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {!editingAmounts && (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => setEditingAmounts(true)}
            data-testid="soren-weekly-cost-edit"
          >
            Edit
          </button>
        )}
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void confirm()} data-testid="soren-weekly-cost-confirm">
          Confirm
        </button>
        {prompt.show && !reply && (
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void dismiss()} data-testid="soren-weekly-cost-dismiss">
            Not now
          </button>
        )}
      </div>
    </div>
  )
}
